import * as Sentry from "@sentry/react";
import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { App } from "antd5";

import { assertCurrentUser } from "lib/currentUser";
import { graphql } from "lib/generated/app-service-gql";
import {
  BidQuestionGroupsQuery,
  UpsertBidReportQuestionsMutation,
  UpsertBidReportQuestionsMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { useGraphQlMutation } from "../../useGraphQlClient";

const upsertBidReportQuestions = graphql(`
  mutation UpsertBidReportQuestions($req: UpsertBidReportQuestionsRequest!) {
    upsertBidReportQuestions(UpsertBidReportQuestionsRequest: $req) {
      groups {
        name
        questions {
          id
          title
          question
          groupName
          promptId
          order
          teamId
          companyId
          autoQualify
        }
      }
    }
  }
`);

export function useUpsertBidReportQuestions(
  options?: UseMutationOptions<
    UpsertBidReportQuestionsMutation,
    unknown,
    UpsertBidReportQuestionsMutationVariables,
    unknown
  >,
) {
  const queryClient = useQueryClient();

  const teamId = assertCurrentUser().team.id;

  const { message } = App.useApp();
  const { data, ...rest } = useGraphQlMutation(upsertBidReportQuestions, {
    ...options,
    onSuccess: async (data, vars, ctx) => {
      await queryClient.invalidateQueries(["bid_questions_by_team", teamId]);
      queryClient.setQueryData(
        ["bid_questions", teamId],
        (_prevData: BidQuestionGroupsQuery | undefined) => {
          return {
            bidQuestionGroups: data.upsertBidReportQuestions,
          };
        },
      );
      options?.onSuccess?.(data, vars, ctx);
      message.success("Successfully updated bid questions");
    },
    onError: async (err, vars, ctx) => {
      options?.onError?.(err, vars, ctx);
      Sentry.captureException(`Error updating bid questions: ${JSON.stringify(err)}`);
      message.error(
        "Unable to update your bid questions; please contact us if the problem persists",
      );
    },
  });

  return { data: data?.upsertBidReportQuestions, ...rest };
}
